// Created by jarongallo at 6/9/21
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { SERVICE_REQUESTS, USER_ROLE_EDITOR } from '../../../../../utils/Config/Constants';
import { getIsUserAdminSelector } from '../../../../../redux/selectors/CurrentUserSelectors';
import Queries from '../../../../../utils/API/Queries';
import UIIconButton from '../../../../../components/UI/UIIconButton';
import { PlusIcon } from '../../../../../assets/icons';
import { toast } from 'react-toastify';
import { GET_SERVICE_REQUEST_ADDRESS, GET_SERVICE_REQUEST_EDITOR } from './queries';
import UIUser from '../../../../../components/UI/UITeamMember/UITeamMember';
import UIModal from '../../../../../components/UI/UIModal';
import UIOverlayCard from '../../../../../components/UI/UIOverlayCard';
import UIUserSearch from '../../../../../components/UI/UIUserSearch/UIUserSearch';
import * as strings from './strings';
import { UI_ICON_BUTTON_FILL_TYPE } from '../../../../../components/UI/UIIconButton/config';

export default function ServiceRequestEditorTab({ serviceRequestId }: ServiceRequestEditorTabProps) {
	const isUserAdmin = useSelector(getIsUserAdminSelector);
	const [serviceRequest, setServiceRequest] = useState<ServiceRequestType>();
	const [property, setProperty] = useState<PropertyType>();
	const [showAddEditorOverlay, setShowAddEditorOverlay] = useState<boolean>(false);
	const [isUpdatingEditor, setIsUpdatingEditor] = useState<boolean>(false);
	const [selectedEditor, setSelectedEditor] = useState<UserType>();

	const [updateServiceRequestCall] = useMutation(Queries.UPDATE_SERVICE_REQUEST);

	const [getServiceRequestService, { data: serviceRequestServiceData }] = useLazyQuery(GET_SERVICE_REQUEST_ADDRESS);

	const [getServiceRequests, { data: serviceRequestData }] = useLazyQuery(GET_SERVICE_REQUEST_EDITOR, {
		variables: { id: serviceRequestId }
	});

	// Mark: Lifecycle functions

	useEffect(() => {
		(async () => {
			await getServiceRequests();
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (serviceRequestData) {
				setServiceRequest(serviceRequestData.serviceRequest);
				let variables = { id: serviceRequestData.serviceRequest.service.property.id };
				await getServiceRequestService({ variables });
			}
		})();
	}, [serviceRequestData]);

	useEffect(() => {
		(() => {
			if (serviceRequestServiceData) {
				setProperty(serviceRequestServiceData.property);
			}
		})();
	}, [serviceRequestServiceData]);

	if (!serviceRequest) return null;

	const updateServiceRequestEditor = async (key, value: string | null) => {
		let variables = { id: serviceRequestId, [key]: value };
		const response = await updateServiceRequestCall({ variables });
		const { updateServiceRequest } = response['data'];
		if (updateServiceRequest) {
			toast.success(strings.addEditorSuccess);
			await getServiceRequests();
		}
	};

	const renderAddEditorModal = () => {
		const hideModal = () => setShowAddEditorOverlay(false);

		const saveEditor = async () => {
			if (!selectedEditor) {
				toast.error(strings.addEditorError);
			} else {
				setIsUpdatingEditor(true);
				await updateServiceRequestEditor('editor', selectedEditor.id);
				setIsUpdatingEditor(false);
				setSelectedEditor(undefined);
				hideModal();
			}
		};

		return (
			<UIModal condition={showAddEditorOverlay}>
				<UIOverlayCard hideModal={hideModal} cardStyle={{ minHeight: '100%' }} title={strings.assignEditorTitle}>
					<UIUserSearch
						emptySetText={strings.emptySetEditors}
						style={{ marginTop: 16 }}
						role={USER_ROLE_EDITOR}
						placeholder={strings.assignEditorPlaceholder}
						selectedUser={selectedEditor}
						onSelectUser={(selectedEditor) => setSelectedEditor(selectedEditor)}
					/>
					<div className='general-buttons-container'>
						<UIIconButton
							dark
							isDisabled={selectedEditor === undefined}
							isLoading={isUpdatingEditor}
							onClick={saveEditor}
							type={UI_ICON_BUTTON_FILL_TYPE}
							text={strings.assignEditorButton}
						/>
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};

	return (
		<div className='uiServiceRequest__top'>
			<div className='uiServiceRequest__header'>
				{serviceRequest.editor ? (
					<UIUser
						user={serviceRequest.editor}
						deleteOnClick
						onClick={isUserAdmin && serviceRequest.status !== SERVICE_REQUESTS.status.complete ? () => updateServiceRequestEditor('editor', null) : undefined}
					/>
				) : (
					<UIIconButton dark icon={PlusIcon.grey} text={strings.addEditorButton} onClick={() => setShowAddEditorOverlay(true)} />
				)}
			</div>
			{renderAddEditorModal()}
		</div>
	);
}
